import { useState } from 'react'
import { Card, CardBody, Chip } from '@heroui/react'
import { ChevronDown, ChevronUp, AlertTriangle, Shield, Info } from 'lucide-react'
import type { AlertTemplate, AlertSeverity } from '../../types'

interface TemplateCardProps {
  template: AlertTemplate
  onToggleActive: (templateId: string, isActive: boolean) => void
}

function getSeverityColor(severity: AlertSeverity): 'danger' | 'warning' | 'primary' | 'default' {
  switch (severity) {
    case 'critical': return 'danger'
    case 'high': return 'warning'
    case 'medium': return 'primary'
    case 'low': return 'default'
  }
}

function getSeverityIcon(severity: AlertSeverity) {
  switch (severity) {
    case 'critical': return <AlertTriangle className="w-4 h-4" />
    case 'high': return <Shield className="w-4 h-4" />
    case 'medium': return <Info className="w-4 h-4" />
    case 'low': return <Info className="w-4 h-4" />
  }
}

function getFrequencyColor(frequency: string): 'success' | 'warning' | 'danger' {
  switch (frequency) {
    case 'rare': return 'success'
    case 'occasional': return 'warning'
    case 'frequent': return 'danger'
    default: return 'warning'
  }
}

export function TemplateCard({ template, onToggleActive }: TemplateCardProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card className="border border-default-200 animate-fade-slide-in">
      <CardBody className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h4 className="text-sm font-semibold text-foreground">{template.name}</h4>
              <Chip
                size="sm"
                color={getSeverityColor(template.severity)}
                variant="flat"
                startContent={getSeverityIcon(template.severity)}
              >
                {template.severity.toUpperCase()}
              </Chip>
              <Chip size="sm" color={getFrequencyColor(template.frequency)} variant="bordered">
                {template.frequency}
              </Chip>
              <Chip size="sm" variant="flat" className="bg-default-100">
                {template.conditionsCount} condition{template.conditionsCount !== 1 ? 's' : ''}
              </Chip>
            </div>
            <p className="text-sm text-default-600 leading-relaxed">{template.triggerDescription}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <input
              type="checkbox"
              checked={template.isActive}
              onChange={(e) => onToggleActive(template.id, e.target.checked)}
              aria-label={`${template.isActive ? 'Deactivate' : 'Activate'} ${template.name}`}
              className="w-3.5 h-3.5 rounded border-tf-border text-primary focus:ring-primary cursor-pointer"
            />
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-default-100 rounded-md transition-colors"
              aria-label={expanded ? 'Collapse details' : 'Expand details'}
            >
              {expanded ? <ChevronUp className="w-4 h-4 text-default-400" /> : <ChevronDown className="w-4 h-4 text-default-400" />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-3 border-t border-default-200 space-y-3 animate-slide-down">
            <div>
              <p className="text-xs font-semibold text-default-500 uppercase mb-1">Business Impact</p>
              <p className="text-sm text-default-700">{template.businessImpact}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-default-500 uppercase mb-1">Recommended Action</p>
              <p className="text-sm text-default-700">{template.recommendedAction}</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
